import { Fragment } from 'react'

import { useIntl, FormattedNumber } from 'react-intl'
import { styled } from 'styled-components'

import { Hazmat, WeightInPounds } from '@b-stock/fe-core/bstock-next/components'
import { getDimensionPalletSpaces } from '@b-stock/fe-core/bstock-next/helpers'
import {
  type LeanGroup,
  type OrchestratedListingDetails,
} from '@b-stock/fe-core/bstock-next/queries'
import { FormattedMessage } from '@b-stock/fe-core/bstock-react'
import { Breakpoints, Colors, Typography } from '@b-stock/fe-core/design-system'
import { ShipmentEnums } from '@b-stock/fe-core/locale'
import type { GroupDimensions } from '@b-stock/listing-api-client'
import { GroupDtoTypeEnum } from '@b-stock/listing-api-client'

import {
  DetailsTitle,
  DetailsValue,
  Pair,
  TableContainer,
  Table,
  Title,
} from '@/buyer-portal/components/features/shared'

import {
  renderDimensionsContent,
  renderPalletSpacesContent,
  renderWeightContent,
  useSellerCollectsShipping,
  useShipmentDetails,
  type DimensionColumnVisibility,
} from './useShipmentDetails'

interface ShipmentProps {
  groups: LeanGroup[]
  details: OrchestratedListingDetails
  hazmatOnly?: boolean
}

const DimsTable = styled.div<{ $columns: number }>`
  width: max-content;
  height: max-content;
  display: grid;
  gap: 0.5rem 1.5rem;
  grid-template-columns: ${(o) => ' max-content'.repeat(o.$columns)};

  @media ${Breakpoints.max.small} {
    display: none;
  }
`

const FlexContainer = styled.div`
  display: flex;
  @media ${Breakpoints.max.medium} {
    flex-direction: column-reverse;
  }
  overflow-x: auto;
  flex-wrap: wrap;
  gap: 2rem;
`

const ShipmentNote = styled.div`
  white-space: pre-line;
  font-weight: normal;
`

// Responsive components that work for both desktop and mobile
const ResponsivePair = styled(Pair)`
  /* Desktop styles from original Pair */

  @media ${Breakpoints.max.small} {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    margin-bottom: 1.5rem;
  }
`

const ResponsiveTable = styled(Table)`
  /* Desktop styles from original Table */

  @media ${Breakpoints.max.small} {
    display: flex;
    flex-direction: column;
    gap: 0;
  }
`

const ResponsiveFieldLabel = styled(DetailsTitle)`
  /* Desktop styles from original DetailsTitle */

  @media ${Breakpoints.max.small} {
    ${Typography.Body3}
    color: ${Colors.Semantic.Neutral.dark_grey};
  }
`

const ResponsiveFieldValue = styled(DetailsValue)`
  /* Desktop styles from original DetailsValue */

  @media ${Breakpoints.max.small} {
    ${Typography.Body2}
    color: ${Colors.Semantic.Neutral.black};
    font-weight: 600;
  }
`

const DesktopDimensionValue = styled(DetailsValue)`
  @media ${Breakpoints.max.small} {
    display: none;
  }
`

const MobileDimensionContainer = styled.div`
  display: none;
  @media ${Breakpoints.max.small} {
    display: flex;
    flex-direction: column;
    gap: 0;
  }
`

const HazmatContainer = styled.div<{ $hazmatOnly?: boolean }>`
  > div {
    margin-top: ${(props) => (props.$hazmatOnly ? '0' : '2rem')};
    padding-top: ${(props) => (props.$hazmatOnly ? '0' : '2rem')};
    border-top: ${(props) => (props.$hazmatOnly ? 'none' : 'inherit')};
    @media ${Breakpoints.max.medium} {
      border-top: none;
      padding-top: 0;
    }
  }
`

const ResponsiveDimensionRow = ({
  columnVisibility,
  groupType,
  dim,
}: {
  columnVisibility: DimensionColumnVisibility
  groupType: GroupDtoTypeEnum
  dim: GroupDimensions
}) => {
  const palletSpaces = getDimensionPalletSpaces(groupType, dim)

  return (
    <>
      {/* Desktop: Use grid layout (original behavior) */}
      <DesktopDimensionValue data-testid="Dimensions">
        {renderDimensionsContent(groupType, dim)}
      </DesktopDimensionValue>

      {columnVisibility.weight ? (
        <DesktopDimensionValue data-testid="Weight">
          {renderWeightContent(dim)}
        </DesktopDimensionValue>
      ) : null}

      {columnVisibility.palletSpaces ? (
        <DesktopDimensionValue data-testid="PalletSpaces">
          {renderPalletSpacesContent(palletSpaces)}
        </DesktopDimensionValue>
      ) : null}
    </>
  )
}

const MobileDimensionRows = ({
  groups,
  columnVisibility,
}: {
  groups: LeanGroup[]
  columnVisibility: DimensionColumnVisibility
}) => {
  return (
    <MobileDimensionContainer>
      {groups.map((group) =>
        group.dimensions?.map((dim, index) => {
          const palletSpaces = getDimensionPalletSpaces(group.type, dim)
          return (
            // There is no unique value on dim to use as key
            // eslint-disable-next-line react/no-array-index-key
            <div key={`${group._id}-${index}`}>
              <ResponsivePair>
                <ResponsiveFieldLabel>
                  <FormattedMessage id="Auction.shipment.details.dimensions" />
                </ResponsiveFieldLabel>
                <ResponsiveFieldValue>
                  {renderDimensionsContent(group.type, dim)}
                </ResponsiveFieldValue>
              </ResponsivePair>
              {columnVisibility.weight ? (
                <ResponsivePair>
                  <ResponsiveFieldLabel>
                    <FormattedMessage id="Auction.shipment.details.weight" />
                  </ResponsiveFieldLabel>
                  <ResponsiveFieldValue>
                    {renderWeightContent(dim)}
                  </ResponsiveFieldValue>
                </ResponsivePair>
              ) : null}
              {columnVisibility.palletSpaces ? (
                <ResponsivePair>
                  <ResponsiveFieldLabel>
                    <FormattedMessage id="Auction.shipment.details.palletSpaces" />
                  </ResponsiveFieldLabel>
                  <ResponsiveFieldValue>
                    {renderPalletSpacesContent(palletSpaces)}
                  </ResponsiveFieldValue>
                </ResponsivePair>
              ) : null}
            </div>
          )
        })
      )}
    </MobileDimensionContainer>
  )
}

export const Shipment = ({
  groups,
  details,
  hazmatOnly = false,
}: ShipmentProps) => {
  const { formatMessage } = useIntl()
  const {
    allowedShipmentTypes,
    columnVisibility,
    firstGroup,
    isHazardous,
    numberOfUnits,
    showOnlyMessage,
    totalPalletSpaces,
    totalWeightInGrams,
    transportMode,
    truckCount,
  } = useShipmentDetails(details, groups)
  const sellerCollectsShipping = useSellerCollectsShipping(details)

  return (
    <>
      <TableContainer>
        <Title>
          <FormattedMessage id="Auction.title.shipmentInformation" />
        </Title>
        <div>
          <FlexContainer>
            {!hazmatOnly && (
              <ResponsiveTable>
                <ResponsivePair>
                  <ResponsiveFieldLabel>
                    <FormattedMessage id="Auction.shipment.details.shipmentType" />
                  </ResponsiveFieldLabel>
                  <ResponsiveFieldValue>
                    {allowedShipmentTypes
                      .map((type) =>
                        ShipmentEnums.ShipmentType.format(formatMessage, type)
                      )
                      .join(', ')}
                    {showOnlyMessage && (
                      <FormattedMessage id="ShippingAvailable.only" />
                    )}
                  </ResponsiveFieldValue>
                </ResponsivePair>

                <ResponsivePair>
                  <ResponsiveFieldLabel>
                    <FormattedMessage id="Auction.shipment.details.transportMode" />
                  </ResponsiveFieldLabel>
                  {!!transportMode && (
                    <ResponsiveFieldValue>
                      <FormattedMessage
                        id={`Auction.shipment.details.transportModes.${transportMode}`}
                      />
                    </ResponsiveFieldValue>
                  )}
                </ResponsivePair>

                <ResponsivePair>
                  <ResponsiveFieldLabel>
                    <FormattedMessage id="Auction.shipment.details.numberOfShipments" />
                  </ResponsiveFieldLabel>
                  <ResponsiveFieldValue>1</ResponsiveFieldValue>
                </ResponsivePair>

                {firstGroup?.type ? (
                  <ResponsivePair>
                    <ResponsiveFieldLabel>
                      <FormattedMessage id="Auction.shipment.details.packagingType" />
                    </ResponsiveFieldLabel>
                    <ResponsiveFieldValue>
                      <FormattedMessage
                        id={`Auction.GroupType.${firstGroup.type}`}
                      />
                    </ResponsiveFieldValue>
                  </ResponsivePair>
                ) : null}

                {(numberOfUnits &&
                  firstGroup?.type !== GroupDtoTypeEnum.FloorLoaded) ||
                numberOfUnits > 1 ? (
                  <ResponsivePair>
                    <ResponsiveFieldLabel>
                      <FormattedMessage id="Auction.shipment.details.numberOfUnits" />
                    </ResponsiveFieldLabel>
                    <ResponsiveFieldValue data-testid="numberOfUnits">
                      <FormattedNumber value={numberOfUnits} />
                    </ResponsiveFieldValue>
                  </ResponsivePair>
                ) : null}

                {totalWeightInGrams ? (
                  <ResponsivePair>
                    <ResponsiveFieldLabel>
                      <FormattedMessage id="Auction.shipment.details.shipmentWeight" />
                    </ResponsiveFieldLabel>
                    <ResponsiveFieldValue>
                      <WeightInPounds grams={totalWeightInGrams} />
                    </ResponsiveFieldValue>
                  </ResponsivePair>
                ) : null}

                <ResponsivePair>
                  <ResponsiveFieldLabel>
                    <FormattedMessage id="Auction.shipment.details.truckCount" />
                  </ResponsiveFieldLabel>
                  <ResponsiveFieldValue>
                    <FormattedNumber value={truckCount ?? 1} />
                  </ResponsiveFieldValue>
                </ResponsivePair>

                {/* Who owns freight, stated per storefront. B-Stock is not
                    always the answer on a Seller Collects lot, and this grid
                    previously implied it was. */}
                {sellerCollectsShipping.isSellerCollects ? (
                  <>
                    <ResponsivePair>
                      <ResponsiveFieldLabel>
                        <FormattedMessage id="Listing.shipping.sellerCollects.arrangedByLabel" />
                      </ResponsiveFieldLabel>
                      <ResponsiveFieldValue data-testid="freight-arranged-by">
                        <FormattedMessage
                          id={sellerCollectsShipping.arrangedById}
                          values={{
                            sellerName: sellerCollectsShipping.sellerName,
                          }}
                        />
                      </ResponsiveFieldValue>
                    </ResponsivePair>
                    <ResponsivePair>
                      <ResponsiveFieldLabel>
                        <FormattedMessage id="Listing.shipping.sellerCollects.collectedByLabel" />
                      </ResponsiveFieldLabel>
                      <ResponsiveFieldValue data-testid="freight-collected-by">
                        <FormattedMessage
                          id={sellerCollectsShipping.collectedById}
                          values={{
                            hasFee: sellerCollectsShipping.hasFee
                              ? 'true'
                              : 'false',
                            sellerName: sellerCollectsShipping.sellerName,
                          }}
                        />
                      </ResponsiveFieldValue>
                    </ResponsivePair>
                  </>
                ) : null}

                {details.shipping?.note ? (
                  <ResponsivePair>
                    <ResponsiveFieldLabel>
                      <FormattedMessage id="Auction.shipment.details.additionalInformation" />
                    </ResponsiveFieldLabel>
                    <ResponsiveFieldValue>
                      <ShipmentNote>{details.shipping.note}</ShipmentNote>
                    </ResponsiveFieldValue>
                  </ResponsivePair>
                ) : null}
              </ResponsiveTable>
            )}

            {!hazmatOnly &&
            firstGroup?.type === GroupDtoTypeEnum.FloorLoaded ? (
              columnVisibility.palletSpaces ? (
                <ResponsivePair>
                  <ResponsiveFieldLabel>
                    <FormattedMessage id="Auction.shipment.details.palletSpaces" />
                  </ResponsiveFieldLabel>
                  <ResponsiveFieldValue>
                    <FormattedNumber value={totalPalletSpaces} />
                  </ResponsiveFieldValue>
                </ResponsivePair>
              ) : null
            ) : (
              !hazmatOnly && (
                <>
                  <DimsTable
                    $columns={
                      1 +
                      (columnVisibility.weight ? 1 : 0) +
                      (columnVisibility.palletSpaces ? 1 : 0)
                    }
                  >
                    <DetailsTitle>
                      <FormattedMessage id="Auction.shipment.details.dimensions" />
                    </DetailsTitle>
                    {columnVisibility.weight ? (
                      <DetailsTitle>
                        <FormattedMessage id="Auction.shipment.details.weight" />
                      </DetailsTitle>
                    ) : null}
                    {columnVisibility.palletSpaces ? (
                      <DetailsTitle>
                        <FormattedMessage id="Auction.shipment.details.palletSpaces" />
                      </DetailsTitle>
                    ) : null}

                    {groups.map((group) => (
                      <Fragment key={group._id}>
                        {group.dimensions?.map((dim, index) => (
                          <ResponsiveDimensionRow
                            // eslint-disable-next-line react/no-array-index-key
                            key={index}
                            columnVisibility={columnVisibility}
                            groupType={group.type}
                            dim={dim}
                          />
                        ))}
                      </Fragment>
                    ))}
                  </DimsTable>

                  <MobileDimensionRows
                    groups={groups}
                    columnVisibility={columnVisibility}
                  />
                </>
              )
            )}

            {isHazardous ? (
              <HazmatContainer $hazmatOnly={hazmatOnly}>
                <Hazmat listing={details} topBorder />
              </HazmatContainer>
            ) : null}
          </FlexContainer>
        </div>
      </TableContainer>
    </>
  )
}
